import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axiosInstance from '../../utils/axiosInstance';

const isNotificationRead = (notification) => {
  const readFlag =
    notification?.isRead ??
    notification?.read ??
    notification?.is_read ??
    notification?.seen ??
    notification?.isSeen;

  if (typeof readFlag === 'string') {
    return ['1', 'true', 'yes', 'read'].includes(readFlag.toLowerCase());
  }

  if (typeof readFlag === 'number') {
    return readFlag === 1;
  }

  if (typeof readFlag === 'boolean') {
    return readFlag;
  }

  return Boolean(notification?.readAt || notification?.read_at);
};

const normalizeNotifications = (payload) => {
  const root = payload?.data ?? payload ?? {};
  const records =
    root?.records ??
    root?.notifications ??
    root?.items ??
    (Array.isArray(root) ? root : []);

  return {
    notifications: Array.isArray(records) ? records : [],
    pagination: root?.pagination || {},
    unreadCount:
      root?.unreadCount ??
      root?.pagination?.unreadCount ??
      (Array.isArray(records)
        ? records.filter((notification) => !isNotificationRead(notification)).length
        : 0),
  };
};

const initialState = {
  notifications: [],
  loading: false,
  error: null,
  pagination: {},
  unreadCount: 0,
  selectedNotification: null,
  currentFetchRequestId: null,
};

export const fetchUserNotifications = createAsyncThunk(
  'userNotifications/fetch',
  async (
    { type = 'all', page = 1, limit = 10 } = {},
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.get('/api/admin/notifications', {
        params: { type, page, limit },
      });

      return normalizeNotifications(response.data);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to load notifications');
    }
  }
);

export const markNotificationAsRead = createAsyncThunk(
  'userNotifications/markRead',
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `/api/admin/notifications/${id}/mark-read`
      );

      return {
        id,
        data: response.data?.data ?? response.data ?? null,
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to mark notification as read');
    }
  }
);

export const markAllNotificationsAsRead = createAsyncThunk(
  'userNotifications/markAllRead',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/api/admin/notifications/mark-read-all');

      return response.data?.data ?? response.data ?? null;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to mark all notifications as read');
    }
  }
);

const userNotificationsSlice = createSlice({
  name: 'userNotifications',
  initialState,
  reducers: {
    clearSelectedNotification: (state) => {
      state.selectedNotification = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserNotifications.pending, (state, action) => {
        state.currentFetchRequestId = action.meta.requestId;
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserNotifications.fulfilled, (state, action) => {
        // Polling and an immediate language refresh can overlap. Keep the result
        // from the most recently started request so an older response cannot
        // replace notifications translated for the newly selected language.
        if (state.currentFetchRequestId && state.currentFetchRequestId !== action.meta.requestId) {
          return;
        }
        state.loading = false;
        state.notifications = action.payload.notifications;
        state.pagination = action.payload.pagination;
        state.unreadCount = action.payload.unreadCount;
      })
      .addCase(fetchUserNotifications.rejected, (state, action) => {
        if (state.currentFetchRequestId && state.currentFetchRequestId !== action.meta.requestId) {
          return;
        }
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(markNotificationAsRead.fulfilled, (state, action) => {
        const notificationId = action.payload?.id;
        state.notifications = state.notifications.map((notification) => {
          const currentId = notification?.id ?? notification?._id;
          if (String(currentId) !== String(notificationId)) {
            return notification;
          }

          return {
            ...notification,
            isRead: true,
            read: true,
            is_read: true,
            seen: true,
            isSeen: true,
            readAt: notification?.readAt || new Date().toISOString(),
            read_at: notification?.read_at || new Date().toISOString(),
          };
        });
        state.selectedNotification = {
          ...(state.selectedNotification || {}),
          ...action.payload?.data,
        };
        state.unreadCount = state.notifications.filter((notification) => !isNotificationRead(notification)).length;
      })
      .addCase(markNotificationAsRead.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(markAllNotificationsAsRead.fulfilled, (state) => {
        state.notifications = state.notifications.map((notification) => ({
          ...notification,
          isRead: true,
          read: true,
          is_read: true,
          seen: true,
          isSeen: true,
          readAt: notification?.readAt || new Date().toISOString(),
          read_at: notification?.read_at || new Date().toISOString(),
        }));
        state.unreadCount = 0;
      })
      .addCase(markAllNotificationsAsRead.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const { clearSelectedNotification } = userNotificationsSlice.actions;

export default userNotificationsSlice.reducer;
